import { Injectable, Injector } from '@angular/core';
import * as Sentry from '@sentry/angular-ivy';

import { ConnectivityService } from './connectivity.service';
import { KeychainService } from './keychain.service';
import { Events } from './events.service';

@Injectable({
  providedIn: 'root',
})
export class VoipCallPublicService {

  private connectivityService: ConnectivityService;

  private keychainService: KeychainService;

  constructor(private events: Events, private injector: Injector) {
    this.connectivityService = this.injector.get(ConnectivityService);
    this.keychainService = this.injector.get(KeychainService);
  }

  init(): Promise<any> {
    return new Promise(async (resolve, reject) => {
      try {
        if (!this.connectivityService) {
          this.connectivityService = this.injector.get(ConnectivityService);
        }

        // Event listener

        this.connectivityService.feathers.service('video-chat-public').on('updated', async (data) => {

          console.log('UPDATED');
          console.log(data);

          if (data.notification_type == 'voip_call_request_accepted') {
            this.events.publish('voip-call-public:request-accepted', data);

          } else if (data.notification_type == 'voip_call_request_cancelled') {
            this.events.publish('voip-call-public:request-cancelled', data);

          } else if (data.notification_type == 'voip_call_request_sent') {
            this.events.publish('voip-call-public:request-sent', data);

          } else if (data.notification_type == 'voip_call_request_declined') {
            this.events.publish('voip-call-public:request-declined', data);

          } else if (data.notification_type == 'voip_call_transcript_sent') {
            this.events.publish('voip-call-public:transcript-sent', data);

          } else if (data.notification_type == 'voip_call_message_send') {
            this.events.publish('voip-call-public:message-sent', data);

          } else if (data.notification_type == 'voip_call_disconnected') {
            this.events.publish('voip-call-public:disconnected', data);
          }
        });

        resolve({});
      } catch (error) {
        Sentry.captureException(error);
        console.error(error);
        reject(error);
      }
    });
  }

  destroy() {
    return new Promise(async (resolve, reject) => {
      this.connectivityService.feathers.service('video-chat-public').removeListener('updated');
      resolve({});
    });
  }

  getMeetingInfos(meetingId: string): Promise<any> {
    Sentry.addBreadcrumb({
      message: 'connectAdmin',
      category: 'voip-call-public.service',
      data: {
        action: 'connect-admin',
        meeting_id: meetingId
      },
      level: 'info'
    });

    return this.connectivityService.feathers.service('video-chat-public').get(meetingId, {});
  }

  generateMeetingId(): Promise<any> {
    Sentry.addBreadcrumb({
      message: 'generateMeetingId',
      category: 'voip-call-public.service',
      data: {
        action: 'generate-meeting-id'
      },
      level: 'info'
    });

    return this.connectivityService.feathers.service('video-chat-public').create({
      action: 'generate-meeting-id'
    }, {});
  }

  inviteUser(callId: string, friendId: string, fullname: string, isAnonymous: boolean, email: string): Promise<any> {
    Sentry.addBreadcrumb({
      message: 'inviteUser',
      category: 'voip-call-public.service',
      data: {
        action: 'invite-user'
      },
      level: 'info'
    });

    return this.connectivityService.feathers.service('video-chat-public').create({
      action: 'invite-user',
      call_id: callId,
      friend_id: friendId,
      fullname: fullname,
      is_anonymous: isAnonymous,
      email: email
    }, {});
  }

  async connectAdmin(callId: string, audioOnly: boolean): Promise<any> {
    const deviceId = await this.keychainService.get('device_id');

    Sentry.addBreadcrumb({
      message: 'connectAdmin',
      category: 'voip-call-public.service',
      data: {
        action: 'connect-admin',
        callId: callId,
        audioOnly: audioOnly
      },
      level: 'info'
    });

    return this.connectivityService.feathers.service('video-chat-public').create({
      action: 'connect-admin',
      call_id: callId,
      audio_only: audioOnly
    }, {});
  }

  async sendRequest(fullname: string, language: string, callId: string): Promise<any> {
    const deviceId = await this.keychainService.get('device_id');

    Sentry.addBreadcrumb({
      message: 'sendRequest',
      category: 'voip-call-public.service',
      data: {
        action: 'send-request',
        full_name: fullname,
        language: language,
        call_id: callId,
        device_id: deviceId
      },
      level: 'info'
    });

    console.log({
      action: 'send-request',
      full_name: fullname,
      language: language,
      call_id: callId,
      device_id: deviceId
    });

    return this.connectivityService.feathers.service('video-chat-public').create({
      action: 'send-request',
      full_name: fullname,
      language: language,
      call_id: callId,
      device_id: deviceId
    }, {});
  }

  async acceptRequest(callId: string, friendId: string): Promise<any> {
    Sentry.addBreadcrumb({
      message: 'acceptRequest',
      category: 'voip-call-public.service',
      data: {
        action: 'accept-request',
        call_id: callId,
        friend_id: friendId
      },
      level: 'info'
    });

    return this.connectivityService.feathers.service('video-chat-public').create({
      action: 'accept-request',
      call_id: callId,
      friend_id: friendId
    }, {});
  }

  async declineRequest(callId: string, friendId: string): Promise<any> {
    Sentry.addBreadcrumb({
      message: 'declineRequest',
      category: 'voip-call-public.service',
      data: {
        action: 'decline-request',
        call_id: callId,
        friend_id: friendId
      },
      level: 'info'
    });

    return this.connectivityService.feathers.service('video-chat-public').create({
      action: 'decline-request',
      call_id: callId,
      friend_id: friendId
    }, {});
  }

  async cancelRequest(callId: string): Promise<any> {
    Sentry.addBreadcrumb({
      message: 'cancelRequest',
      category: 'voip-call-public.service',
      data: {
        action: 'cancel-request',
        call_id: callId
      },
      level: 'info'
    });

    return this.connectivityService.feathers.service('video-chat-public').create({
      action: 'cancel-request',
      call_id: callId
    }, {});
  }

  async sendMessage(callId: string, text = '', type: 'text' | 'animoji'): Promise<any> {
    Sentry.addBreadcrumb({
      message: 'sendMessage',
      category: 'voip-call-public.service',
      data: {
        action: 'send-message',
        call_id: callId,
        text: text,
        type: type
      },
      level: 'info'
    });

    return this.connectivityService.feathers.service('video-chat-public').create({
      action: 'send-message',
      call_id: callId,
      text: text,
      type: type
    }, {});
  }

  async endCall(callId: string): Promise<any> {
    Sentry.addBreadcrumb({
      message: 'endCall',
      category: 'voip-call-public.service',
      data: {
        action: 'end-call',
        call_id: callId
      },
      level: 'info'
    });

    return this.connectivityService.feathers.service('video-chat-public').create({
      action: 'end-call',
      call_id: callId
    }, {});
  }
}
